import React, { useEffect, useMemo, useState } from "react";
import { slugify } from "@utils/common";
import {
    Stack, TextField, Switch, FormControlLabel, InputLabel, Select, MenuItem,
    FormControl, Box, Button, Typography, Divider, Chip, Autocomplete, Paper, LinearProgress
} from "@mui/material";
import { useForm, Controller, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import apiService from "@services/common";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import ImageUploadWithPreview from "@components/imageUpload";

/* ----------------- Helpers ----------------- */
const defaultSeo = {
    focus_keyword: "",
    related_keywords: [],
    slug: "",
    meta_title: "",
    meta_description: "",
    canonical_url: "",
    robots: "index,follow",
    og_image: null,
    twitter_card: "summary_large_image",
};

const MAX_TITLE = 60;
const MIN_TITLE = 30;
const MAX_DESC = 160;
const MIN_DESC = 70;

function stripHtml(html = "") {
    if (!html) return "";
    const withoutTags = html
        .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
        .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
        .replace(/<[^>]+>/g, " ");
    return withoutTags.replace(/\s+/g, " ").trim();
}
function buildCanonical(base, slug) {
    const root = base && base !== "/" ? base.replace(/\/+$/, "") : "";
    return `${root}/bai-viet/${slug}`;
}
function escapeRegExp(s = "") {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
function countWords(plain = "") {
    if (!plain) return 0;
    const tokens = plain.trim().split(/\s+/);
    return tokens.filter(Boolean).length;
}
function countOccurrences(text = "", word = "") {
    if (!text || !word) return 0;
    const pattern = new RegExp(`\\b${escapeRegExp(word)}\\b`, "giu"); // đủ tốt cho VN
    const matches = text.match(pattern);
    return matches ? matches.length : 0;
}
function readabilityScore(plain = "") {
    if (!plain) return 100;
    const sentences = plain.split(/[.!?]+/).filter(Boolean);
    const sentenceCount = Math.max(1, sentences.length);
    const words = plain.trim().split(/\s+/).filter(Boolean);
    const wordCount = Math.max(1, words.length);

    const avgWordsPerSentence = wordCount / sentenceCount;
    const avgWordLen =
        words.reduce((sum, w) => sum + (w ? w.length : 0), 0) / wordCount;

    let score = 100;
    if (avgWordsPerSentence > 20) score -= Math.min(40, (avgWordsPerSentence - 20) * 2);
    if (avgWordLen > 6) score -= Math.min(30, (avgWordLen - 6) * 5);
    return Math.max(0, Math.min(100, Math.round(score)));
}
function scoreSeo(checks, flags) {
    let score = 0;
    const W = {
        focus_present: 20,
        has_headings: 10,
        has_h1: 5,
        has_h2: 5,
        has_images_with_alt: 10,
        link_balance: 10,
        density_ok: 15,
        long_enough: 15,
        related_hits: 10,
    };
    if (flags.focus_present) score += W.focus_present;
    if (flags.has_headings) score += W.has_headings;
    if (checks.has_h1) score += W.has_h1;
    if (checks.has_h2) score += W.has_h2;
    if (flags.has_images_with_alt) score += W.has_images_with_alt;
    if (flags.link_balance) score += W.link_balance;
    if (flags.density_ok) score += W.density_ok;
    if (flags.long_enough) score += W.long_enough;

    const rel = Number(checks.related_hits || 0);
    if (rel > 0) score += Math.min(W.related_hits, Math.ceil(Math.log2(1 + rel) * 3));
    return Math.max(0, Math.min(100, score));
}
/* --------------- /Helpers ------------------ */

const schema = yup.object({
    title: yup.string().required("Bắt buộc"),
    summary: yup.string().nullable(),
    content: yup.string().nullable(),
    post_category_id: yup.mixed().nullable(),
    is_public: yup.boolean().default(false),

    focus_keyword: yup.string().nullable(),
    related_keywords: yup.array().of(yup.string()).default([]),
    slug: yup.string().required("Slug là bắt buộc"),
    meta_title: yup.string().nullable(),
    meta_description: yup.string().nullable(),
    canonical_url: yup.string().nullable(),

    robots: yup
        .string()
        .oneOf(["index,follow", "noindex,nofollow", "index,nofollow", "noindex,follow"])
        .default("index,follow"),

    twitter_card: yup
        .string()
        .oneOf(["summary", "summary_large_image"])
        .default("summary_large_image"),
});

function GoogleSnippetPreview({ title, url, description }) {
    return (
        <Paper variant="outlined" sx={{ p: 2, bgcolor: "background.paper" }}>
            <Box sx={{ color: "primary.main", fontSize: 18, lineHeight: 1.2, mb: 0.5 }}>
                {title || "(Tiêu đề xem trước)"}
            </Box>
            <Box sx={{ color: "success.dark", fontSize: 14, mb: 0.5 }}>
                {url || "https://example.com/bai-viet/slug"}
            </Box>
            <Box sx={{ color: "text.secondary", fontSize: 14 }}>
                {description || "Mô tả xem trước hiển thị khoảng 1–2 dòng..."}
            </Box>
        </Paper>
    );
}

const AddDrawer = ({ submitFunction = () => { }, appUrl = "" }) => {
    const [category, setCategory] = useState([]);

    // Trường SEO đã sửa tay => không auto
    const [seoEdited, setSeoEdited] = useState({
        slug: false,
        meta_title: false,
        meta_description: false,
        focus_keyword: false,
        canonical_url: false,
    });

    // SEO LIVE SCORE — NEW
    const [seoScore, setSeoScore] = useState(0);
    const [readScore, setReadScore] = useState(100);
    const [seoChecks, setSeoChecks] = useState({
        word_count: 0,
        focus_density_percent: 0,
        focus_in_title: false,
        has_h1: false,
        has_h2: false,
        internal_links: 0,
        external_links: 0,
        image_count: 0,
        image_alt_missing: 0,
        related_hits: 0,
    });

    useEffect(() => {
        apiService
            .get("post-category")
            .then((res) => setCategory(res.data.data))
            .catch((err) => console.log(err));
    }, []);

    const {
        register,
        handleSubmit,
        control,
        setValue,
        formState: { errors },
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            title: "",
            summary: "",
            content: "",
            post_category_id: "",
            image: null,
            is_public: false,
            ...defaultSeo,
        },
    });

    // Watch fields
    const title = useWatch({ control, name: "title" }) || "";
    const summary = useWatch({ control, name: "summary" }) || "";
    const content = useWatch({ control, name: "content" }) || "";
    const slug = useWatch({ control, name: "slug" }) || "";
    const metaTitle = useWatch({ control, name: "meta_title" }) || "";
    const metaDesc = useWatch({ control, name: "meta_description" }) || "";
    const robots = useWatch({ control, name: "robots" }) || "index,follow";
    const focusKeyword = useWatch({ control, name: "focus_keyword" }) || "";
    const relatedKeywords = useWatch({ control, name: "related_keywords" }) || [];
    const twitterCard = useWatch({ control, name: "twitter_card" }) || "summary_large_image";
    const canonical = useWatch({ control, name: "canonical_url" }) || "";

    // Auto-suggest SEO từ title/summary/content
    useEffect(() => {
        const s = slugify(title || "");
        if (!seoEdited.slug) setValue("slug", s, { shouldValidate: true });
        if (!seoEdited.meta_title) setValue("meta_title", title ? title : "", { shouldValidate: false });
        if (!seoEdited.meta_description) {
            const base = summary && summary.trim() ? summary.trim() : stripHtml(content).slice(0, MAX_DESC);
            setValue("meta_description", base, { shouldValidate: false });
        }
        if (!seoEdited.focus_keyword) {
            const plainTitle = (title || "").trim();
            const fk = plainTitle.split(" ").slice(0, 4).join(" ");
            setValue("focus_keyword", fk, { shouldValidate: false });
        }
        if (!seoEdited.canonical_url) {
            const sSlug = s || slug;
            setValue("canonical_url", buildCanonical(appUrl, sSlug), { shouldValidate: false });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [title, summary, content]);

    const titleLen = (metaTitle || "").length;
    const descLen = (metaDesc || "").length;

    // ---- SEO LIVE SCORE — NEW: phân tích client-side (debounce ~200ms) ----
    useEffect(() => {
        const id = setTimeout(() => {
            const html = content || "";
            const plain = stripHtml(html);

            // bóc H1/H2, link, img thô bằng regex đơn giản (đủ xài cho live preview)
            const h1s = (html.match(/<h1[^>]*>([\s\S]*?)<\/h1>/gi) || []).map((m) =>
                stripHtml(m)
            );
            const h2s = (html.match(/<h2[^>]*>([\s\S]*?)<\/h2>/gi) || []).map((m) =>
                stripHtml(m)
            );
            const hasH1 = h1s.length > 0;
            const hasH2 = h2s.length > 0;

            const imgTags = html.match(/<img\b[^>]*>/gi) || [];
            const imageCount = imgTags.length;
            const imageAltMissing = imgTags.filter((img) => !/alt\s*=\s*["'][^"']+["']/i.test(img)).length;

            const links = html.match(/<a\b[^>]*href=["'][^"']+["'][^>]*>/gi) || [];
            let internal = 0, external = 0;
            links.forEach((a) => {
                const hrefMatch = a.match(/href=["']([^"']+)["']/i);
                const href = hrefMatch ? hrefMatch[1] : "";
                if (/^https?:\/\//i.test(href)) external++;
                else internal++;
            });

            const wordCount = countWords(plain);
            const fk = (focusKeyword || "").trim();

            const focusInTitle = fk ? countOccurrences(metaTitle || title, fk) > 0 : false;
            const focusInH1H2 = fk ? countOccurrences([...h1s, ...h2s].join(" "), fk) > 0 : false;
            const focusCount = fk ? countOccurrences(plain, fk) : 0;

            let relatedHits = 0;
            (relatedKeywords || []).forEach((kw) => {
                if (!kw) return;
                relatedHits += countOccurrences(plain, kw);
            });

            const density = wordCount > 0 ? Math.round((focusCount / wordCount) * 10000) / 100 : 0; // %

            const checks = {
                word_count: wordCount,
                focus_density_percent: density,
                focus_in_title: !!focusInTitle,
                has_h1: hasH1,
                has_h2: hasH2,
                internal_links: internal,
                external_links: external,
                image_count: imageCount,
                image_alt_missing: imageAltMissing,
                related_hits: relatedHits,
            };
            setSeoChecks(checks);

            const flags = {
                focus_present: !!fk && (focusInTitle || focusInH1H2 || focusCount > 0),
                has_headings: hasH1 || hasH2,
                has_images_with_alt: imageCount > 0 && imageAltMissing === 0,
                link_balance: internal + external > 0,
                density_ok: density >= 0.5 && density <= 2.0,
                long_enough: wordCount >= 300,
            };

            setSeoScore(scoreSeo(checks, flags));
            setReadScore(readabilityScore(plain));
        }, 200);
        return () => clearTimeout(id);
    }, [title, metaTitle, content, summary, focusKeyword, relatedKeywords]);

    const onSubmit = (data) => {
        const payload = {
            ...data,
            related_keywords: (data.related_keywords || []).filter((x) => x && x.trim() !== ""),
            // optional: gửi kèm điểm để log UI (backend vẫn tự chấm lại)
            _client_seo_score: seoScore,
            _client_readability_score: readScore,
        };
        submitFunction(payload);
    };

    return (
        <Box sx={{ maxWidth: 900 }}>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
                Thêm mới bài viết
            </Typography>

            <Stack spacing={3}>
                {/* --- Khối bài viết --- */}
                <Box>
                    <Stack direction="column" spacing={2}>
                        <TextField
                            label="Tiêu đề"
                            fullWidth
                            size="small"
                            {...register("title")}
                            error={!!errors.title}
                            helperText={errors.title?.message}
                        />

                        <TextField
                            label="Tóm tắt"
                            fullWidth
                            multiline
                            size="small"
                            rows={4}
                            {...register("summary")}
                            error={!!errors.summary}
                            helperText={errors.summary?.message}
                        />

                        <Controller
                            name="content"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <ReactQuill theme="snow" value={field.value} onChange={field.onChange} />
                            )}
                        />

                        <Controller
                            name="post_category_id"
                            control={control}
                            defaultValue=""
                            render={({ field }) => (
                                <FormControl fullWidth>
                                    <InputLabel id="cat-label">Chọn danh mục</InputLabel>
                                    <Select
                                        size="small"
                                        labelId="cat-label"
                                        value={field.value}
                                        label="Chọn danh mục"
                                        onChange={field.onChange}
                                    >
                                        {category.map((item) => (
                                            <MenuItem key={item.id} value={item.id}>
                                                {item.title}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />

                        <Controller
                            name="image"
                            control={control}
                            render={({ field }) => (
                                <ImageUploadWithPreview value={field.value} onChange={field.onChange} />
                            )}
                        />
                        {errors.image && <Box color="red" mt={1}>{errors.image?.message}</Box>}

                        <Controller
                            name="is_public"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel control={<Switch {...field} checked={field.value} />} label="Công khai" />
                            )}
                        />
                    </Stack>
                </Box>

                <Divider />

                {/* --- Khối SEO --- */}
                <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        SEO cho bài viết
                    </Typography>

                    {/* SEO LIVE SCORE — NEW */}
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            Điểm SEO: {seoScore}/100
                        </Typography>
                        <LinearProgress variant="determinate" value={seoScore} sx={{ mb: 1 }} />
                        <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                            Độ dễ đọc: {readScore}/100
                        </Typography>
                        <LinearProgress variant="determinate" value={readScore} />

                        <Stack spacing={0.5} sx={{ mt: 1 }}>
                            <Typography variant="caption">
                                • Số từ: <b>{seoChecks.word_count}</b>
                            </Typography>
                            <Typography variant="caption">
                                • Mật độ từ khóa: <b>{seoChecks.focus_density_percent}%</b> (khuyến nghị 0.5–2.0%)
                            </Typography>
                            <Typography variant="caption">
                                • Focus trong tiêu đề: <b>{seoChecks.focus_in_title ? "Có" : "Không"}</b>
                            </Typography>
                            <Typography variant="caption">
                                • H1/H2: <b>{seoChecks.has_h1 ? "Có H1" : "Không H1"}</b>, <b>{seoChecks.has_h2 ? "Có H2" : "Không H2"}</b>
                            </Typography>
                            <Typography variant="caption">
                                • Ảnh: <b>{seoChecks.image_count}</b> (thiếu alt: <b>{seoChecks.image_alt_missing}</b>)
                            </Typography>
                            <Typography variant="caption">
                                • Liên kết: nội bộ <b>{seoChecks.internal_links}</b> / ngoài <b>{seoChecks.external_links}</b>
                            </Typography>
                            <Typography variant="caption">
                                • Related hits: <b>{seoChecks.related_hits}</b>
                            </Typography>
                        </Stack>
                    </Paper>

                    <Stack spacing={2}>
                        {/* Slug */}
                        {(() => {
                            const { onChange: slugOnChange, ...slugReg } = register("slug");
                            return (
                                <TextField
                                    label="Slug"
                                    size="small"
                                    fullWidth
                                    {...slugReg}
                                    error={!!errors.slug}
                                    helperText={errors.slug?.message || "Tự sinh theo tiêu đề; có thể sửa."}
                                    onChange={(e) => {
                                        setSeoEdited((s) => ({ ...s, slug: true }));
                                        const clean = slugify(e.target.value);
                                        // cập nhật lại cho RHF bằng giá trị đã slugify
                                        slugOnChange({ ...e, target: { ...e.target, value: clean } });
                                        if (!seoEdited.canonical_url) {
                                            setValue("canonical_url", buildCanonical(appUrl, clean));
                                        }
                                    }}
                                    onFocus={() => setSeoEdited((s) => ({ ...s, slug: true }))}
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{ spellCheck: false }}
                                />
                            );
                        })()}

                        {/* Focus keyword */}
                        <TextField
                            label="Từ khóa chính (focus keyword)"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ spellCheck: false }}
                            {...register("focus_keyword")}
                            onFocus={() => setSeoEdited((s) => ({ ...s, focus_keyword: true }))}
                        />

                        {/* Related keywords */}
                        <Controller
                            name="related_keywords"
                            control={control}
                            render={({ field }) => (
                                <Autocomplete
                                    multiple
                                    freeSolo
                                    size="small"
                                    value={field.value || []}
                                    onChange={(_, value) => field.onChange(value)}
                                    options={[]}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                variant="outlined"
                                                label={option}
                                                {...getTagProps({ index })}
                                                key={`${option}-${index}`}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} label="Từ khóa liên quan (enter để thêm)" />
                                    )}
                                />
                            )}
                        />

                        {/* Meta Title */}
                        <TextField
                            label="Meta title"
                            size="small"
                            fullWidth
                            inputProps={{ maxLength: MAX_TITLE }}
                            {...register("meta_title")}
                            helperText={`${(metaTitle || "").length}/${MAX_TITLE} ký tự — chuẩn nhất ${MIN_TITLE}–${MAX_TITLE}`}
                            onFocus={() => setSeoEdited((s) => ({ ...s, meta_title: true }))}
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Meta Description */}
                        <TextField
                            label="Meta description"
                            size="small"
                            fullWidth
                            multiline
                            rows={3}
                            inputProps={{ maxLength: MAX_DESC }}
                            {...register("meta_description")}
                            helperText={`${(metaDesc || "").length}/${MAX_DESC} ký tự — chuẩn nhất ${MIN_DESC}–${MAX_DESC}`}
                            onFocus={() => setSeoEdited((s) => ({ ...s, meta_description: true }))}
                            InputLabelProps={{ shrink: true }}
                        />

                        {/* Canonical + Robots + Twitter Card */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                            <TextField
                                label="Canonical URL"
                                size="small"
                                fullWidth
                                value={canonical}
                                onChange={(e) => {
                                    setSeoEdited((s) => ({ ...s, canonical_url: true }));
                                    setValue("canonical_url", e.target.value);
                                }}
                                InputLabelProps={{ shrink: true }}
                            />
                            <FormControl fullWidth>
                                <InputLabel id="robots-label">Robots</InputLabel>
                                <Select
                                    labelId="robots-label"
                                    size="small"
                                    value={robots}
                                    label="Robots"
                                    onChange={(e) => setValue("robots", e.target.value)}
                                >
                                    <MenuItem value="index,follow">index,follow</MenuItem>
                                    <MenuItem value="noindex,nofollow">noindex,nofollow</MenuItem>
                                    <MenuItem value="index,nofollow">index,nofollow</MenuItem>
                                    <MenuItem value="noindex,follow">noindex,follow</MenuItem>
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel id="tw-card-label">Twitter Card</InputLabel>
                                <Select
                                    labelId="tw-card-label"
                                    size="small"
                                    value={twitterCard}
                                    label="Twitter Card"
                                    onChange={(e) => setValue("twitter_card", e.target.value)}
                                >
                                    <MenuItem value="summary_large_image">summary_large_image</MenuItem>
                                    <MenuItem value="summary">summary</MenuItem>
                                </Select>
                            </FormControl>
                        </Stack>

                        {/* OG Image */}
                        <Controller
                            name="og_image"
                            control={control}
                            render={({ field }) => (
                                <Box>
                                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                                        OG Image
                                    </Typography>
                                    <ImageUploadWithPreview value={field.value} onChange={field.onChange} />
                                </Box>
                            )}
                        />

                        {/* Snippet Preview */}
                        <Box sx={{ mt: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                Xem trước kết quả tìm kiếm
                            </Typography>
                            <GoogleSnippetPreview
                                title={metaTitle || title}
                                url={canonical || buildCanonical(appUrl, slug)}
                                description={metaDesc || (summary || stripHtml(content)).slice(0, MAX_DESC)}
                            />
                        </Box>
                    </Stack>
                </Box>

                {/* --- Submit --- */}
                <Box>
                    <Button variant="contained" size="small" onClick={handleSubmit(onSubmit)}>
                        Lưu lại
                    </Button>
                </Box>
            </Stack>
        </Box>
    );
};

export default AddDrawer;
